import {
	escapeAttribute,
	escapeText,
	PROPS,
	readProps,
	stringProp,
} from "./layout-props"

// Pure, so the schema, the v2 markdown reader and the importer all write the
// same markup without pulling Tiptap into the portal's render path.

export const MIN_TILE_COLUMNS = 1
export const MAX_TILE_COLUMNS = 4
export const DEFAULT_TILE_COLUMNS = 3

export function clampTileColumns(
	value: unknown,
	fallback = DEFAULT_TILE_COLUMNS,
): number {
	const parsed = Number(value)
	if (!Number.isFinite(parsed) || parsed <= 0) return fallback
	return Math.min(
		MAX_TILE_COLUMNS,
		Math.max(MIN_TILE_COLUMNS, Math.round(parsed)),
	)
}

export const TILE_BLOCK = new RegExp(
	`<Tile${PROPS}(?:\\/>|>([\\s\\S]*?)<\\/Tile>)`,
	"g",
)

/** A `Columns` block that holds tiles; one holding cards stays a card group. */
export const TILE_COLUMNS_BLOCK = new RegExp(
	`<Columns${PROPS}>((?:(?!<\\/Columns>)[\\s\\S])*?<Tile\\b[\\s\\S]*?)<\\/Columns>`,
	"g",
)

// Mintlify pairs a light and a dark preview by class; our image node keeps one.
const DARK_ONLY_IMAGE =
	/<img\b(?=[^>]*\bclass(?:Name)?\s*=\s*["'][^"']*\bdark:(?:mint-)?block\b)[^>]*>/g

export const dropDarkOnlyImages = (source: string): string =>
	source.replace(DARK_ONLY_IMAGE, "")

export interface TileParts {
	href: string | null
	titleHtml: string
	description: string
	previewHtml: string
}

export function tileHtml({
	href,
	titleHtml,
	description,
	previewHtml,
}: TileParts): string {
	const title = href
		? `<a href="${escapeAttribute(href)}">${titleHtml}</a>`
		: titleHtml
	return `<div data-type="tile" class="pl-tile"><div data-tile-preview class="pl-tile-preview">${
		previewHtml || "<p></p>"
	}</div><p data-tile-title class="pl-tile-title">${title}</p><p data-tile-description class="pl-tile-description">${escapeText(
		description,
	)}</p></div>`
}

export function tileGroupHtml(tiles: string[], cols: number): string {
	const columns = clampTileColumns(cols)
	return `<div data-type="tile-group" class="pl-tile-group" data-cols="${columns}" style="--cols: ${columns}">${tiles.join(
		"",
	)}</div>`
}

/**
 * Every tile in a body, rendered with the caller's markdown parser: `inline`
 * for the title (Mintlify allows bold, italic and code there), `block` for the
 * preview.
 */
export function tilesFrom(
	source: string,
	parse: {
		inline: (markdown: string) => string
		block: (markdown: string) => string
	},
): string[] {
	const tiles: string[] = []
	for (const match of source.matchAll(new RegExp(TILE_BLOCK.source, "g"))) {
		const props = readProps(match[1] ?? "")
		const body = dropDarkOnlyImages(match[2] ?? "")
		const title = stringProp(props, "title") ?? ""
		tiles.push(
			tileHtml({
				href: stringProp(props, "href"),
				titleHtml: title ? parse.inline(title) : "",
				description: stringProp(props, "description") ?? "",
				previewHtml: body.trim() ? parse.block(body) : "",
			}),
		)
	}
	return tiles
}

export const readTileColumns = (rawProps: string): number =>
	clampTileColumns(stringProp(readProps(rawProps), "cols"))
